/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { Disclosure } from '@headlessui/react'
import classNames from 'classnames'
import type { Identifier } from 'dnd-core'
import { Star } from 'iconoir-react'
import { ReactElement, useRef } from 'react'
import { useDrag, useDrop, XYCoord } from 'react-dnd'
import { ChevronToggle } from '../ChevronToggle'
import DraggableIcon from '../Icon/DraggableIcon'
import UnvisibleIcon from '../Icon/UnvisibleIcon'
import VisibleIcon from '../Icon/VisibleIcon'
import { Tooltip } from 'app/components/Tooltip'
import { useListCopy } from 'app/hooks/useListCopy'
import { SectionItem, isSectionDraggable, hasVisibleChildren } from 'lib/contactDrawerConfig'

export interface DragItem {
  section: SectionItem
  index: number
  parentId?: string
}

export function DraggableSection({
  section,
  index,
  parentId,
  onDrop,
  onToggleVisibility,
  onToggleSpotlight,
  isNested = false,
  nestingLevel = 0,
  isIntegrated,
  contact,
}: {
  section: SectionItem
  index: number
  parentId?: string
  onDrop: (draggedId: string, targetId: string, dragParentId?: string, targetParentId?: string) => void
  onToggleVisibility: (id: string, parentId?: string) => void
  onToggleSpotlight: (id: string, parentId?: string) => void
  isNested?: boolean
  nestingLevel?: number
  isIntegrated?: boolean
  contact?: any
}): ReactElement {
  const ref = useRef<HTMLDivElement>(null)
  // The section table in `contactDrawerConfig` is static data (it is also persisted as a
  // saved order), so the memberships section is renamed where it is drawn rather than there.
  const copy = useListCopy()
  const sectionName = section.id === `pipelines` ? copy.NounPlural : section.name

  const [{ handlerId, isAnyDragging }, dropRef] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null; isAnyDragging: boolean }
  >({
    accept: `section`,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isAnyDragging: monitor.getItem() !== null,
      }
    },
    hover: (item: DragItem, monitor) => {
      if (!ref.current) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex && item.parentId === parentId) return

      if (item.parentId !== parentId) {
        if (!(item.parentId === undefined && parentId === undefined) && item.parentId !== parentId) {
          return
        }
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onDrop(item.section.id, section.id, item.parentId, parentId)
      item.index = hoverIndex
      item.parentId = parentId
    },
  })

  const isDraggable = isSectionDraggable(section.id)
  const hasVisibleChildrenLocal = hasVisibleChildren(section, isIntegrated, contact)
  const canMoveToSpotlight = !section.children || hasVisibleChildrenLocal

  const [{ isDragging }, dragRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => ({ section, index, parentId }),
    type: `section`,
    canDrag: isDraggable,
  })

  if (isDraggable) {
    dragRef(dropRef(ref))
  } else {
    dropRef(ref)
  }

  return (
    <Disclosure
      as={`div`}
      ref={ref}
      data-handler-id={handlerId}
      className={classNames(
        `mb-1 rounded-md bg-surface`,
        isDragging ? `opacity-0` : `opacity-100`,
        // Mobile: fluid widths so the tree fits a 375px modal; md: restores the
        // exact desktop pixel widths (indent + width sum flush at each level).
        nestingLevel === 0
          ? `w-full border border-subtle px-2 py-1 shadow-elevation-1 md:w-[404px]`
          : nestingLevel === 1
            ? `ml-4 w-[calc(100%-1rem)] md:w-[372px]`
            : `ml-8 w-[calc(100%-2rem)] md:w-[340px]`,
      )}
      defaultOpen={hasVisibleChildrenLocal ? true : false}
    >
      {({ open }) => (
        <>
          <Disclosure.Button
            className={classNames(
              `group relative flex h-8 w-full items-center`,
              isDraggable ? `cursor-draggable cursor-grab active:cursor-grabbing` : `cursor-default`,
              nestingLevel > 0 && !isAnyDragging && `hover:rounded-[4px] hover:bg-surface-subtle`,
            )}
          >
            <div className="flex items-center gap-x-3">
              <div className="px-[7px] py-[5px]">
                <DraggableIcon
                  className={isDraggable ? `cursor-grab text-minimal` : `cursor-not-allowed text-minimal opacity-40`}
                />
              </div>
              <span
                className={classNames(
                  !isNested ? `text-label-sm` : `text-body-sm`,
                  !section.visible ? `text-minimal` : !isNested ? `text-strong` : `text-default`,
                )}
              >
                {sectionName}
              </span>
              {section.id === `spotlight` &&
                section.children &&
                section.children.filter((child) => child.originalParentId).length > 0 && (
                  <Star
                    width={16}
                    height={16}
                    strokeOpacity={section.children ? 0 : 1}
                    className="ml-2 fill-amber-600 stroke-amber-600"
                  />
                )}
            </div>
            {hasVisibleChildrenLocal ? (
              <div className={`absolute flex items-center ${nestingLevel === 0 ? `right-1` : `right-[5px]`}`}>
                <ChevronToggle open={open} className="pr-0 text-default" />
              </div>
            ) : nestingLevel > 0 ? (
              <div className="absolute right-1.5 flex items-center gap-x-1">
                <Tooltip message={section.visible ? `Hide` : `Show`} className="!cursor-pointer">
                  <div
                    className={classNames(
                      `cursor-pointer rounded`,
                      !section.visible ? `text-minimal` : `text-subtle`,
                      section.visible ? (isAnyDragging ? `hidden` : `hidden group-hover:block`) : `block`,
                    )}
                    onClick={() => onToggleVisibility(section.id, parentId)}
                  >
                    {section.visible ? (
                      <VisibleIcon width={16} height={16} />
                    ) : (
                      <UnvisibleIcon width={16} height={16} />
                    )}
                  </div>
                </Tooltip>
                {/* Star icon - fixed right position */}
                <Tooltip
                  message={
                    section.isCore
                      ? `Required for Spotlight`
                      : section.isSpotlight
                        ? `Remove from Spotlight`
                        : `Add to Spotlight`
                  }
                  className={section.isCore ? `!cursor-default` : `!cursor-pointer`}
                >
                  <div
                    className={classNames(
                      `h-4 w-4 items-center justify-center`,
                      canMoveToSpotlight && (section.isCore || section.isSpotlight)
                        ? `visible`
                        : canMoveToSpotlight
                          ? classNames(
                              isAnyDragging ? `hidden` : `hidden cursor-pointer rounded group-hover:block`,
                              section.isSpotlight ? `text-yellow-600` : `text-subtle`,
                              `!cursor-pointer`,
                            )
                          : `invisible`,
                      section.isSpotlight ? `text-yellow-600` : `text-subtle`,
                    )}
                    onClick={!section.isCore ? () => onToggleSpotlight(section.id, parentId) : undefined}
                  >
                    <Star
                      width={16}
                      height={16}
                      strokeOpacity={section.isSpotlight ? 0 : 1}
                      className={
                        section.isCore
                          ? `fill-amber-500/[0.12] stroke-amber-500/[0.12] text-amber-500`
                          : section.isSpotlight
                            ? `fill-amber-600 stroke-amber-600`
                            : ``
                      }
                    />
                  </div>
                </Tooltip>
              </div>
            ) : null}
          </Disclosure.Button>
          <Disclosure.Panel>
            {hasVisibleChildrenLocal && section.children && (
              <>
                {section.children.map((child, childIndex) => (
                  <DraggableSection
                    key={child.id}
                    section={child}
                    index={childIndex}
                    parentId={section.id}
                    onDrop={onDrop}
                    onToggleVisibility={onToggleVisibility}
                    onToggleSpotlight={onToggleSpotlight}
                    isNested={true}
                    nestingLevel={nestingLevel + 1}
                    isIntegrated={isIntegrated}
                    contact={contact}
                  />
                ))}
              </>
            )}
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}
